import { lazy, Suspense } from 'react';
import type { LotteryAnswerPayload } from '@todo/shared';
import { formatSaleDate } from '../../lib/lottery-format';
import { LotteryChatTable } from './LotteryChatTable';
import { ReportLinkCard } from './ReportLinkCard';

/** recharts only when an answer HAS a chart. */
const LotteryChatChart = lazy(() => import('./LotteryChatChart'));

const PERSONA_LABEL: Record<string, string> = { analyst: 'Analyst', marketing: 'Marketing head', advisor: 'Business advisor' };

/**
 * What a Lottery analyst answer shows below its text (Phase 8): its charts,
 * tables and report links in the order the analyst made them, and a footer
 * with "Figures up to" and the persona. The grounding check is NOT shown in
 * this phase.
 */
/**
 * "Understood as: ..." (9 Oct 2026): how a FOLLOW-UP was understood, in plain words, under the answer - only when it
 * differs from what was typed. No codes.
 */
export function understoodLine(payload: LotteryAnswerPayload): string | null {
  const u = payload.understood;
  if (!u || (u.kind !== 'follow_up' && u.kind !== 'display')) return null;
  const shown = u.shown.replace(/\s*\((?:\d{3,5}|[A-Z0-9_]{6,})(?:,\s*[^)]*)?\)/g, '').replace(/\b[A-Z]+(?:_[A-Z0-9]+)+\b/g, '').replace(/\s{2,}/g, ' ').trim();
  const same = (a: string) => a.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  // A subject carried over from the previous answer is always said (12 Oct 2026), even when the words look alike.
  return shown && (same(shown) !== same(u.original) || (u.carried?.length ?? 0) > 0) ? shown : null;
}

/** Where the answer came from (13 Oct 2026): every data answer says it in its footer. */
const PATH_LABEL: Record<string, string> = {
  template: 'sales reports',
  free_sql: 'analyst query on the sales data',
  advisor: 'adviser, from the sales data',
  server: 'sales reports',
};

export function LotteryAnswer({ payload }: { payload: LotteryAnswerPayload }) {
  const understood = understoodLine(payload);
  return (
    <div className="mt-2 space-y-2">
      {payload.blocks.map((b, i) =>
        b.type === 'table' ? (
          <LotteryChatTable key={`t${i}`} block={b} />
        ) : b.type === 'chart' ? (
          <Suspense key={`c${i}`} fallback={<p className="text-xs text-slate-400">Loading the chart…</p>}>
            <LotteryChatChart block={b} />
          </Suspense>
        ) : (
          <ReportLinkCard key={`r${i}`} block={b} />
        ),
      )}
      {understood && <p className="text-[11px] text-slate-400" data-testid="understood-as">Understood as: {understood}</p>}
      <p className="text-[11px] text-slate-500">
        {payload.figuresUpTo ? `Figures up to ${formatSaleDate(payload.figuresUpTo)}` : 'No figures loaded yet'}
        {' · '}
        {PERSONA_LABEL[payload.persona] ?? payload.persona}
        {payload.conversation?.path ? ` · ${PATH_LABEL[payload.conversation.path] ?? payload.conversation.path}` : ''}
      </p>
    </div>
  );
}
